import React, { useEffect, useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import FormSection from './components/FormSection';
import FormFieldLabel from './components/FormFieldLabel';
import FormRadioButtons from './components/FormRadioButtons';
import FormTextField from './components/FormTextField';
import {
  CHECKLIST_STATUS_OPTIONS,
  PROJECT_FACILITY_OPTIONS,
  PROJECT_SERVICE_OPTIONS,
  PROJECT_TYPE_OPTIONS_OLD,
  YES_NO_OPTIONS,
} from './data';
import FormSelect from './components/FormSelect';
import FormDatePicker from './components/FormDatePicker';
import { getDirtyOrDefinedValues } from './utils';
import {
  uploadProjectCloseoutChecklist,
  upsertProjectCloseoutForm,
} from '../../models/api/project-lifecycle';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectState } from '../../features/project/selectors';
import Button from '@material-ui/core/Button';
import { allowNavigation, blockNavigation } from '../../features/navigation/actions';
import _ from 'lodash';
import { addSnackbar } from '../../features/snackbar/actions';
import CircularLoader from '../loader/CircularLoader';
import {
  CloseoutChecklistStatusType,
  PartialProjectCloseoutSchema,
  ProjectCloseoutChecklistType,
} from '../../api-client/autogenerated';
import { reloadProject } from '../../features/project/actions';
import { InsertDriveFileOutlined, Publish, Save } from '@material-ui/icons';
import { getProjectSummaryById } from '../../models/api/project';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { InputLabel, MenuItem, Select, Tooltip, useMediaQuery } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import FileUploadDialog from '../dialogs/FileUploadDialog';
import { uploadToStaging } from '../../models/api/filesystem';
import { MULTI_PART_FILE_SIZE } from '../../scripts/constants';
import Typography from '@material-ui/core/Typography';
import { downloadFileById, isPdf, openInNewTab, formatDate } from '../../scripts/utils';
import PDFIcon from '../icons/PDF-icon';
import EyeIcon from '@material-ui/icons/Visibility';
import IconButton from '@material-ui/core/IconButton';
import DownloadIcon from '../icons/Download-icon';

const useStyles = makeStyles(() =>
  createStyles({
    printBreakBefore: {
      pageBreakBefore: 'always',
      '@media print': {
        paddingTop: 16,
      },
    },
    hideOnPrint: {
      '@media print': {
        display: 'none',
      },
    },
  }),
);

const schema = z.object({
  projectWon: z.boolean().optional(),
  projectCompletionDate: z.string().optional(),
  finalProjectManager: z.string().optional(),
  hasConstructionOccurred: z.boolean().optional(),
  contractor: z.string().optional(),
  constructionDuration: z.coerce.number().optional(),
  constructionBidEstimate: z.coerce.number().optional(),
  finalConstructionCost: z.coerce.number().optional(),
  totalProjectCost: z.coerce.number().optional(),
  payAppsFiled: z.boolean().optional(),
  drawingsArchived: z.boolean().optional(),
  a3Progress: z.coerce.number().optional(),
  hasBeenPhotographed: z.boolean().optional(),

  finalNewSquareFootage: z.coerce.number().optional(),
  finalRenovatedSquareFootage: z.coerce.number().optional(),
  finalTotalSquareFootage: z.coerce.number().optional(),

  projectType: z.string().optional(),
  projectTypesAddition: z.array(z.string()).optional(),
  facilityTypes: z.array(z.string()).optional(),
  facilityTypesAddition: z.array(z.string()).optional(),
  serviceTypes: z.array(z.string()).optional(),
  serviceTypesAddition: z.array(z.string()).optional(),

  winLossReasoning: z.string().optional(),
  majorEquipment: z.string().optional(),
  marketingPurposes: z.string().optional(),
  updatedProjectDescription: z.string().optional(),
  additionalNotes: z.string().optional(),

  areFinalPaymentsReceived: z.boolean().optional(),
  areConsultantsPaid: z.boolean().optional(),
  isWipReleased: z.boolean().optional(),
  hasCloseoutFeeAdjustments: z.boolean().optional(),
  hasStartCompletionDate: z.boolean().optional(),
  isCloseoutComplete: z.boolean().optional(),
  isReadyToArchive: z.boolean().optional(),
  dateClosed: z.string().optional(),
  billingNotes: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

const defaultValues: FormValues = {
  projectWon: undefined,
  projectCompletionDate: '',
  finalProjectManager: '',
  hasConstructionOccurred: undefined,
  contractor: '',
  constructionDuration: undefined,
  constructionBidEstimate: undefined,
  finalConstructionCost: undefined,
  totalProjectCost: undefined,
  payAppsFiled: undefined,
  drawingsArchived: undefined,
  a3Progress: undefined,
  hasBeenPhotographed: undefined,

  finalNewSquareFootage: undefined,
  finalRenovatedSquareFootage: undefined,
  finalTotalSquareFootage: undefined,

  projectType: '',
  projectTypesAddition: [],
  facilityTypes: [],
  facilityTypesAddition: [],
  serviceTypes: [],
  serviceTypesAddition: [],

  winLossReasoning: '',
  majorEquipment: '',
  marketingPurposes: '',
  updatedProjectDescription: '',
  additionalNotes: '',

  areFinalPaymentsReceived: undefined,
  areConsultantsPaid: undefined,
  isWipReleased: undefined,
  hasCloseoutFeeAdjustments: undefined,
  hasStartCompletionDate: undefined,
  isCloseoutComplete: undefined,
  isReadyToArchive: undefined,
  dateClosed: '',
  billingNotes: '',
};

type SchemaInput = z.input<typeof schema>;
type SchemaOutput = z.output<typeof schema>;

interface Props {
  canEdit: boolean;
  isEditing: boolean;
  publicData?: PartialProjectCloseoutSchema | null;
}

export default function ProjectCloseoutForm(props: Props) {
  const { canEdit, isEditing, publicData } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const project = useSelector(getProjectState);
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { isSubmitting, dirtyFields, isDirty },
  } = useForm<SchemaInput, unknown, SchemaOutput>({
    disabled: !isEditing || !canEdit,
    resolver: zodResolver(schema),
    defaultValues: !publicData && !project?.projectCloseout?.data ? defaultValues : undefined,
    reValidateMode: 'onChange',
  });

  const isMobile = useMediaQuery('(max-width: 940px)');

  const [
    projectCloseoutChecklistUploadDialogOpen,
    setProjectCloseoutChecklistUploadDialogOpen,
  ] = useState(false);
  const [
    billingCloseoutChecklistUploadDialogOpen,
    setBillingCloseoutChecklistUploadDialogOpen,
  ] = useState(false);

  const [projectCloseoutChecklistFile, setProjectCloseoutChecklistFile] = useState<File>();
  const [billingCloseoutChecklistFile, setBillingCloseoutChecklistFile] = useState<File>();

  const [
    projectCloseoutChecklistStagingKey,
    setProjectCloseoutChecklistStagingKey,
  ] = useState<string>();
  const [
    billingCloseoutChecklistStagingKey,
    setBillingCloseoutChecklistStagingKey,
  ] = useState<string>();

  const [
    projectCloseoutChecklistStatus,
    setProjectCloseoutChecklistStatus,
  ] = useState<CloseoutChecklistStatusType | null>(project?.projectCloseoutChecklistStatus || null);
  const [
    billingCloseoutChecklistStatus,
    setBillingCloseoutChecklistStatus,
  ] = useState<CloseoutChecklistStatusType | null>(project?.projectCloseoutChecklistStatus || null);

  const [isSubmittingProjectCloseoutChecklist, setIsSubmittingProjectCloseoutChecklist] = useState(
    false,
  );
  const [isSubmittingBillingCloseoutChecklist, setIsSubmittingBillingCloseoutChecklist] = useState(
    false,
  );

  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleUploadProgress = (event: any) => {
    setUploadProgress(Math.round((100 * event.loaded) / event.total));
  };

  const fillFormFieldsFromProject = async () => {
    if (!project) return;

    if (project.substantialCompletionDate)
      setValue('projectCompletionDate', project.substantialCompletionDate);
    if (project.contractorAdministratorUser)
      setValue('contractor', project.contractorAdministratorUser.name);
    if (project.daysToCompletion)
      setValue('constructionDuration', Math.round(project.daysToCompletion / 30));
    if (project.bidSetup?.estimatedConstructionCost)
      setValue('constructionBidEstimate', project.bidSetup.estimatedConstructionCost / 100);
    if (project.contractAmount) {
      const summary = await getProjectSummaryById(project.id);
      setValue('finalConstructionCost', (project.contractAmount + summary.costAdded) / 100);
    }

    if (project.projectRequest?.data) {
      setValue('projectType', project.projectRequest.data.projectType);
      setValue('serviceTypes', project.projectRequest.data.serviceTypes);
      setValue('facilityTypes', project.projectRequest.data.facilityTypes);
    }
  };

  useEffect(() => {
    if (publicData) {
      reset(publicData);
    } else if (project?.projectCloseout?.data) {
      reset(project.projectCloseout.data);
    } else {
      fillFormFieldsFromProject();
    }
  }, [project, publicData]);

  useEffect(() => {
    if (isDirty) {
      dispatch(blockNavigation());
    } else {
      dispatch(allowNavigation());
    }
  }, [isDirty]);

  const newSf = watch('finalNewSquareFootage');
  const renovatedSf = watch('finalRenovatedSquareFootage');

  useEffect(() => {
    if (newSf !== undefined && renovatedSf !== undefined) {
      setValue('finalTotalSquareFootage', (Number(newSf) || 0) + (Number(renovatedSf) || 0), {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [newSf, renovatedSf]);

  const onSubmit = handleSubmit(
    async (data) => {
      await submit(data);
      dispatch(
        addSnackbar({ id: Date.now(), message: 'Form saved successfully', severity: 'success' }),
      );
      return;
    },
    (errors) => {
      console.log('errors', errors);
    },
  );

  const submit = async (data: FormValues) => {
    if (!project) return;

    const dirtyOrDefinedValues = getDirtyOrDefinedValues<FormValues>(dirtyFields, data);

    const body = {
      ...dirtyOrDefinedValues,
    };

    await upsertProjectCloseoutForm(project.id, body);

    reset(data);

    dispatch(allowNavigation());
    dispatch(reloadProject());

    window.scrollTo({ behavior: 'smooth', top: 0, left: 0 });
  };

  useEffect(() => {
    setProjectCloseoutChecklistStagingKey(undefined);
  }, [project?.projectCloseoutChecklist]);

  useEffect(() => {
    setBillingCloseoutChecklistStagingKey(undefined);
  }, [project?.billingCloseoutChecklist]);

  const stagingUpload = async (type: 'project' | 'billing') => {
    const file = type === 'project' ? projectCloseoutChecklistFile : billingCloseoutChecklistFile;
    const setStagingKey =
      type === 'project'
        ? setProjectCloseoutChecklistStagingKey
        : setBillingCloseoutChecklistStagingKey;
    const closeDialog =
      type === 'project'
        ? () => setProjectCloseoutChecklistUploadDialogOpen(false)
        : () => setBillingCloseoutChecklistUploadDialogOpen(false);

    if (!file) return;

    try {
      setUploadMessage(`Uploading ${file.name}...`);

      const response = await uploadToStaging(
        { fileName: file.name, useMultiPartUpload: file.size > MULTI_PART_FILE_SIZE },
        file,
        handleUploadProgress,
      );

      setStagingKey(response.s3Key);
    } finally {
      closeDialog();
    }
  };

  const submitChecklist = async (type: ProjectCloseoutChecklistType) => {
    if (!project) return;

    const stagingKey =
      type === 'project' ? projectCloseoutChecklistStagingKey : billingCloseoutChecklistStagingKey;
    const status =
      type === 'project' ? projectCloseoutChecklistStatus : billingCloseoutChecklistStatus;
    const setIsSubmitting =
      type === 'project'
        ? setIsSubmittingProjectCloseoutChecklist
        : setIsSubmittingBillingCloseoutChecklist;

    if (!status) return;

    try {
      setIsSubmitting(true);
      await uploadProjectCloseoutChecklist(project.id, { type, stagingKey, status });
      await dispatch(reloadProject());
    } finally {
      setIsSubmitting(false);
    }
  };

  const getChecklistContent = (type: ProjectCloseoutChecklistType) => {
    const checklist =
      type === 'project' ? project?.projectCloseoutChecklist : project?.billingCloseoutChecklist;
    const setDialogOpen =
      type === 'project'
        ? setProjectCloseoutChecklistUploadDialogOpen
        : setBillingCloseoutChecklistUploadDialogOpen;
    const status =
      type === 'project' ? projectCloseoutChecklistStatus : billingCloseoutChecklistStatus;
    const setStatus =
      type === 'project' ? setProjectCloseoutChecklistStatus : setBillingCloseoutChecklistStatus;
    const stagingKey =
      type === 'project' ? projectCloseoutChecklistStagingKey : billingCloseoutChecklistStagingKey;
    const isSubmitting =
      type === 'project'
        ? isSubmittingProjectCloseoutChecklist
        : isSubmittingBillingCloseoutChecklist;

    const fileIcon = (name?: string) => (
      <>
        {isPdf(name) ? (
          <PDFIcon style={{ marginRight: 3 }} />
        ) : (
          <InsertDriveFileOutlined style={{ fontSize: '1.75rem', marginLeft: -3 }} />
        )}
      </>
    );

    return (
      <>
        <FormFieldLabel style={{ alignSelf: 'start', marginTop: 8 }}>
          {type === 'project' ? 'Project' : 'Billing'} Closeout Checklist:
        </FormFieldLabel>
        <div>
          {stagingKey ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {fileIcon(stagingKey)}
                <Typography>{stagingKey.slice(stagingKey.lastIndexOf('/') + 1)}</Typography>
              </div>
              <Typography style={{ fontWeight: 600, color: '#ED3F26', marginTop: 16 }}>
                THIS FILE HAS NOT BEEN SAVED: {!status ? 'SELECT A STATUS' : 'CLICK SAVE'}
              </Typography>
            </>
          ) : checklist ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {fileIcon(checklist.name)}
                <Typography style={{ marginRight: 8 }}>{checklist.name}</Typography>
                {isPdf(checklist.name) ? (
                  <Tooltip title="View File" placement="top" arrow>
                    <IconButton onClick={() => openInNewTab(checklist!.id)} style={{ padding: 0 }}>
                      <EyeIcon />
                    </IconButton>
                  </Tooltip>
                ) : null}
                <Tooltip title="Download File" placement="top" arrow>
                  <IconButton
                    onClick={() => downloadFileById(checklist!.id, checklist!.name)}
                    style={{ padding: 0 }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              </div>
              <Typography style={{ marginTop: 16 }}>
                <strong>Uploaded on: </strong>
                {formatDate(checklist.createdOn, true)}
              </Typography>
              <Typography style={{ marginTop: 16 }}>
                <strong>Submitted By: </strong>
                {checklist.creatorUser?.name},{' '}
                <strong style={{ fontWeight: 500 }}>{checklist.creatorUser?.company?.name}</strong>
              </Typography>
            </>
          ) : (
            <Typography>None</Typography>
          )}
        </div>
        <div />
        {canEdit && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setDialogOpen(true)}
              startIcon={<Publish style={{ marginBottom: 3 }} />}
              style={!isMobile ? { flexShrink: 0 } : {}}
            >
              {checklist ? 'Replace' : 'Upload'}
            </Button>
            <FormControl
              variant="outlined"
              size="small"
              fullWidth
              disabled={!stagingKey && !checklist}
            >
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value as CloseoutChecklistStatusType)}
              >
                {CHECKLIST_STATUS_OPTIONS.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!isSubmitting ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Save />}
                disabled={(!stagingKey && !checklist) || !status}
                onClick={() => submitChecklist(type)}
                style={!isMobile ? { flexShrink: 0 } : {}}
              >
                Save
              </Button>
            ) : (
              <CircularLoader size={25} style={{ width: 235 }} />
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <form noValidate onSubmit={onSubmit} style={{ marginTop: 24 }}>
      <FormSection formGridProps={{ style: { rowGap: 16 } }}>
        {getChecklistContent(ProjectCloseoutChecklistType.Project)}
        {getChecklistContent(ProjectCloseoutChecklistType.Billing)}
      </FormSection>

      {/* Project Info */}
      <FormSection title="Project Info">
        <FormFieldLabel>Project Win/Lose Status?</FormFieldLabel>
        <FormRadioButtons
          name="projectWon"
          control={control}
          options={[
            { label: 'Won', value: true },
            { label: 'Lost', value: false },
          ]}
        />

        <FormFieldLabel>Project Completion Date:</FormFieldLabel>
        <FormDatePicker name="projectCompletionDate" control={control} />

        <FormFieldLabel>Final Grace Design Studios Project Manager:</FormFieldLabel>
        <FormTextField name="finalProjectManager" control={control} />

        <FormFieldLabel>Did Construction occur?</FormFieldLabel>
        <FormRadioButtons
          name="hasConstructionOccurred"
          control={control}
          options={YES_NO_OPTIONS}
        />

        <FormFieldLabel>Contractor:</FormFieldLabel>
        <FormTextField name="contractor" control={control} />

        <FormFieldLabel>Construction Duration:</FormFieldLabel>
        <FormTextField
          name="constructionDuration"
          control={control}
          signedNumericOnly
          suffix="months"
        />

        <FormFieldLabel>Construction Bid/Estimate:</FormFieldLabel>
        <FormTextField
          name="constructionBidEstimate"
          control={control}
          signedNumericOnly
          prefix="$"
        />

        <FormFieldLabel>Final Construction Cost:</FormFieldLabel>
        <FormTextField
          name="finalConstructionCost"
          control={control}
          signedNumericOnly
          prefix="$"
        />

        <FormFieldLabel>Total Project Cost:</FormFieldLabel>
        <FormTextField
          name="totalProjectCost"
          control={control}
          signedNumericOnly
          prefix="$"
          className={classes.printBreakBefore}
        />

        <FormFieldLabel>ALL Pay App's filed?</FormFieldLabel>
        <FormRadioButtons name="payAppsFiled" control={control} options={YES_NO_OPTIONS} />

        <FormFieldLabel>Drawings Archived?</FormFieldLabel>
        <FormRadioButtons name="drawingsArchived" control={control} options={YES_NO_OPTIONS} />

        <FormFieldLabel>A3 Progress:</FormFieldLabel>
        <FormRadioButtons
          name="a3Progress"
          control={control}
          options={[
            { label: '25%', value: 25 },
            { label: '50%', value: 50 },
            { label: '75%', value: 75 },
            { label: '100%', value: 100 },
          ]}
        />

        <FormFieldLabel>Has this project been photographed for marketing?</FormFieldLabel>
        <FormRadioButtons name="hasBeenPhotographed" control={control} options={YES_NO_OPTIONS} />
      </FormSection>

      {/* Final Project Square Footage */}
      <FormSection title="Final Project Square Footage">
        <FormFieldLabel>New SF +/-</FormFieldLabel>
        <FormTextField name="finalNewSquareFootage" control={control} signedNumericOnly />

        <FormFieldLabel>Renovated SF +/-</FormFieldLabel>
        <FormTextField name="finalRenovatedSquareFootage" control={control} signedNumericOnly />

        <FormFieldLabel>Total SF +/-</FormFieldLabel>
        <FormTextField name="finalTotalSquareFootage" control={control} disabled />
      </FormSection>

      {/* Updated Project Types */}
      <FormSection title="Updated Project Types (if necessary)">
        <FormFieldLabel>Original Project Types:</FormFieldLabel>
        <FormSelect
          name="projectType"
          control={control}
          options={PROJECT_TYPE_OPTIONS_OLD}
          disabled
        />

        <FormFieldLabel>Project Type Change:</FormFieldLabel>
        <FormSelect
          name="projectTypesAddition"
          control={control}
          options={PROJECT_TYPE_OPTIONS_OLD}
          multiple
        />

        <FormFieldLabel>Original Facility Types:</FormFieldLabel>
        <FormSelect
          name="facilityTypes"
          control={control}
          options={PROJECT_FACILITY_OPTIONS}
          multiple
          disabled
        />

        <FormFieldLabel>Facility Type Additions:</FormFieldLabel>
        <FormSelect
          name="facilityTypesAddition"
          control={control}
          options={PROJECT_FACILITY_OPTIONS}
          multiple
        />

        <FormFieldLabel>Original Project Scope Types:</FormFieldLabel>
        <FormSelect
          name="serviceTypes"
          control={control}
          options={PROJECT_SERVICE_OPTIONS}
          multiple
          disabled
        />

        <FormFieldLabel>Project Scope Type Additions:</FormFieldLabel>
        <FormSelect
          name="serviceTypesAddition"
          control={control}
          options={PROJECT_SERVICE_OPTIONS}
          multiple
        />
      </FormSection>

      {/* Win/Loss Reasoning */}
      <FormSection title="Win/Loss Reasoning" noGrid className={classes.printBreakBefore}>
        <FormTextField name="winLossReasoning" control={control} large />
      </FormSection>

      {/* Major Equipment */}
      <FormSection title="Major Equipment" noGrid>
        <FormTextField name="majorEquipment" control={control} large />
      </FormSection>

      {/* Unique Goals or Identifying Qualities for Marketing */}
      <FormSection
        title="Unique Goals or Identifying Qualities that can be used for Marketing Purposes: (LEED, EDAC, Charrette Process, Sustainability, Budget, End Users, etc.):"
        noGrid
      >
        <FormTextField name="marketingPurposes" control={control} large />
      </FormSection>

      {/* Updated Project Description */}
      <FormSection title="Updated Project Description" noGrid>
        <FormTextField name="updatedProjectDescription" control={control} large />
      </FormSection>

      {/* Additional Notes */}
      <FormSection title="Additional Notes" noGrid>
        <FormTextField name="additionalNotes" control={control} large />
      </FormSection>

      {/* Billing Closeout */}
      <FormSection title="Billing Closeout" className={classes.printBreakBefore}>
        <FormFieldLabel>Final Payments Received?</FormFieldLabel>
        <FormRadioButtons
          name="areFinalPaymentsReceived"
          control={control}
          options={YES_NO_OPTIONS}
        />

        <FormFieldLabel>Consultants Paid?</FormFieldLabel>
        <FormRadioButtons name="areConsultantsPaid" control={control} options={YES_NO_OPTIONS} />

        <FormFieldLabel>WIP Released?</FormFieldLabel>
        <FormRadioButtons name="isWipReleased" control={control} options={YES_NO_OPTIONS} />

        <FormFieldLabel>Closeout Fee Adjustments?</FormFieldLabel>
        <FormRadioButtons
          name="hasCloseoutFeeAdjustments"
          control={control}
          options={YES_NO_OPTIONS}
        />

        <FormFieldLabel>Start/Completion Date Entered?</FormFieldLabel>
        <FormRadioButtons
          name="hasStartCompletionDate"
          control={control}
          options={YES_NO_OPTIONS}
        />

        <FormFieldLabel>Project Closeout Complete?</FormFieldLabel>
        <FormRadioButtons name="isCloseoutComplete" control={control} options={YES_NO_OPTIONS} />

        <FormFieldLabel>Ready to Archive?</FormFieldLabel>
        <FormRadioButtons name="isReadyToArchive" control={control} options={YES_NO_OPTIONS} />

        <FormFieldLabel>Date Closed?</FormFieldLabel>
        <FormDatePicker name="dateClosed" control={control} />
      </FormSection>

      {/* Billing Closeout */}
      <FormSection title="Billing Notes" noGrid>
        <FormTextField name="billingNotes" control={control} large />
      </FormSection>

      {isEditing ? (
        <div
          className={classes.hideOnPrint}
          style={{
            position: 'sticky',
            bottom: 0,
            width: 'calc(100% + 32px)',
            padding: 16,
            marginLeft: -16,
            backgroundColor: '#FFF',
            borderTop: '1px solid #CCC',
            zIndex: 100,
          }}
        >
          {!isSubmitting ? (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!canEdit}
              startIcon={<Save />}
            >
              Save Changes
            </Button>
          ) : (
            <CircularLoader style={{ justifyContent: 'start', paddingLeft: 32 }} />
          )}
        </div>
      ) : null}
      <FileUploadDialog
        open={projectCloseoutChecklistUploadDialogOpen}
        handleClose={() => setProjectCloseoutChecklistUploadDialogOpen(false)}
        title="Upload Checklist"
        file={projectCloseoutChecklistFile}
        addFile={(f) => setProjectCloseoutChecklistFile(f as File)}
        removeFile={() => setProjectCloseoutChecklistFile(undefined)}
        allowMultiple={false}
        acceptAll
        disableComments
        canSubmit={!!projectCloseoutChecklistFile}
        handleSubmit={() => stagingUpload('project')}
        uploadProgress={uploadProgress}
        uploadMessage={uploadMessage}
        buttonType="submit"
        disableDesignUpload
      />
      <FileUploadDialog
        open={billingCloseoutChecklistUploadDialogOpen}
        handleClose={() => setBillingCloseoutChecklistUploadDialogOpen(false)}
        title="Upload Checklist"
        file={billingCloseoutChecklistFile}
        addFile={(f) => setBillingCloseoutChecklistFile(f as File)}
        removeFile={() => setBillingCloseoutChecklistFile(undefined)}
        allowMultiple={false}
        acceptAll
        disableComments
        canSubmit={!!billingCloseoutChecklistFile}
        handleSubmit={() => stagingUpload('billing')}
        uploadProgress={uploadProgress}
        uploadMessage={uploadMessage}
        buttonType="submit"
        disableDesignUpload
      />
    </form>
  );
}
